// Dev Priority Context Provider

import React, { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@auth';
import type { DevPriorityTicket } from './types';
import * as backlogApi from './api';

interface DevPriorityContextValue {
  tickets: DevPriorityTicket[];
  userType: string;
  loading: boolean;
  error: string | null;
  fetchTickets: () => Promise<void>;
  updateTicketPriority: (ticketId: number, priority: number) => Promise<void>;
  reorderTickets: (ticketId: number, newIndex: number) => Promise<void>;
}

const DevPriorityContext = createContext<DevPriorityContextValue | undefined>(undefined);

export const useDevPriority = () => {
  const context = useContext(DevPriorityContext);
  if (!context) {
    throw new Error('useDevPriority must be used within DevPriorityProvider');
  }
  return context;
};

interface DevPriorityProviderProps {
  children: ReactNode;
}

export const DevPriorityProvider: React.FC<DevPriorityProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [tickets, setTickets] = useState<DevPriorityTicket[]>([]);
  const [userType, setUserType] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTickets = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);
    try {
      const [ticketData, userTypeData] = await Promise.all([
        backlogApi.getDevPriorityTickets(),
        backlogApi.getUserType(),
      ]);
      
      setTickets(ticketData);
      setUserType(userTypeData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch tickets';
      setError(message);
      console.error('Error fetching dev priority tickets:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const updateTicketPriority = useCallback(async (ticketId: number, priority: number) => {
    if (!isAuthenticated) return;

    try {
      await backlogApi.updateTicketPriority(ticketId, priority);
      
      // Update local state
      setTickets((prevTickets) =>
        prevTickets.map((ticket) =>
          ticket.ticketId === ticketId ? { ...ticket, perceivedPriority: priority } : ticket
        )
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update priority';
      setError(message);
      console.error('Error updating ticket priority:', err);
      throw err;
    }
  }, [isAuthenticated]);

  const reorderTickets = useCallback(async (ticketId: number, newIndex: number) => {
    if (!isAuthenticated) return;

    try {
      // Call the backend API which returns the updated ticket list
      const updatedTickets = await backlogApi.reorderTicket(ticketId, newIndex);
      setTickets(updatedTickets);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reorder tickets';
      setError(message);
      console.error('Error reordering tickets:', err);
      throw err;
    }
  }, [isAuthenticated]);

  const value: DevPriorityContextValue = {
    tickets,
    userType,
    loading,
    error,
    fetchTickets,
    updateTicketPriority,
    reorderTickets,
  };

  return (
    <DevPriorityContext.Provider value={value}>
      {children}
    </DevPriorityContext.Provider>
  );
};
